// RigSentinel — Phase 1 Auth/Storage-Scoping.
//
// Gate for every functional route. Public pages (Splash, /download,
// /pricing, /auth, /auth/reset, legal, /contact) are mounted OUTSIDE this
// component in App.js and never pass through here.
import React, { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuthState } from "@/lib/authState";
import { runV2MigrationOnce } from "@/lib/profiles/migrations";
import LegacyDataClaimModal from "@/components/LegacyDataClaimModal";

export default function RequireAuth() {
  const loc = useLocation();
  const { uid, loading } = useAuthState();
  const migratedForUid = useRef(null);

  useEffect(() => {
    if (!uid || migratedForUid.current === uid) return;
    migratedForUid.current = uid;
    // One-shot, idempotent, account-scoped legacy Profile migration — safe to
    // run every time a session mounts; the flag it checks is itself scoped.
    try { runV2MigrationOnce(); } catch { /* best-effort */ }
  }, [uid]);

  // Loading with no seeded marker at all (first-ever visit / fresh device) —
  // brief neutral loading state instead of flashing the sign-in redirect.
  if (loading && !uid) {
    return (
      <div className="py-16 text-center text-sm text-[var(--rs-text-3)]" data-testid="require-auth-loading">
        Loading…
      </div>
    );
  }

  if (!uid) {
    const returnTo = `${loc.pathname}${loc.search || ""}`;
    return <Navigate to={`/auth?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return (
    <>
      <Outlet />
      <LegacyDataClaimModal />
    </>
  );
}
